import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchHybridSearchData } from '../../actions/fetchHybridSearchData';
import { IPublicWebV1SearchResponse } from '../../repositories/dailyrent/entities/response/PublicWebV1SearchResponse';
import { selectHasMoreOffers } from '../../selectors/listingOffers/selectHasMoreOffers';
import { selectHasPrevPage } from '../../selectors/listingOffers/selectHasPrevPage';
import { selectIsOffersLoading } from '../../selectors/listingOffers/selectIsOffersLoading';
import { TThunkDispatch } from '../../types/redux';

interface IUsePaginationActionsArgs {
  onBeforeChange?: () => void;
  onAfterChange?: (response: IPublicWebV1SearchResponse) => void;
}

interface IUsePaginationActionsResult {
  hasPrev: boolean;
  hasNext: boolean;
  isLoading: boolean;
  handlePrev: () => Promise<void>;
  handleNext: () => Promise<void>;
}

export function usePaginationActions({
  onBeforeChange,
  onAfterChange,
}: IUsePaginationActionsArgs = {}): IUsePaginationActionsResult {
  const dispatch = useDispatch<TThunkDispatch>();
  const hasPrev = useSelector(selectHasPrevPage);
  const hasNext = useSelector(selectHasMoreOffers);
  const isLoading = useSelector(selectIsOffersLoading);
  const deviceType = useDeviceType();

  const runChange = React.useCallback(
    async (mode: 'next' | 'prev') => {
      onBeforeChange?.();
      const response = await dispatch(fetchHybridSearchData({ mode, shouldLoadListingOverride: true, deviceType }));

      if (response?.listingOffers) {
        onAfterChange?.(response.listingOffers);
      }
    },
    [dispatch, onBeforeChange, onAfterChange, deviceType],
  );

  const handleNext = React.useCallback(() => runChange('next'), [runChange]);
  const handlePrev = React.useCallback(() => runChange('prev'), [runChange]);

  return {
    hasPrev,
    hasNext,
    isLoading,
    handlePrev,
    handleNext,
  };
}
